import Reveal from "@/components/base/Reveal";
import { externalLinks, images } from "@/mocks/site";

const pillars = [
  "Local food economies that actually hold",
  "Community built around a shared table",
  "Creators, makers and food businesses",
  "Experiences that travel and stay",
];

export function LocavoreIdea() {
  return (
    <section className="bg-secondary-100">
      <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow text-secondary-700">The Idea</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="mt-5 font-heading text-4xl leading-[1.02] text-foreground-950 sm:text-5xl md:text-6xl">
                A MOVEMENT,
                <br />
                <span className="italic text-secondary-700">NOT A MENU.</span>
              </h2>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 lg:self-center">
            <Reveal delay={140}>
              <p className="font-body text-lg leading-relaxed text-foreground-700 md:text-xl">
                Locavore PODS is the ecosystem around the food — a community of
                growers, cooks, creators and food businesses building healthier
                local food economies, one pod at a time.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-5 font-body text-base leading-relaxed text-foreground-700">
                It is the part you never see on a plate: the farms, the markets,
                the makers and the neighbours who decide that where food comes
                from actually matters.
              </p>
            </Reveal>

            <div className="mt-9 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
              {pillars.map((p, i) => (
                <Reveal key={p} delay={260 + i * 80}>
                  <div className="flex items-start gap-3">
                    <span className="mt-2 block h-1.5 w-1.5 shrink-0 rounded-full bg-secondary-600" />
                    <p className="font-body text-sm leading-relaxed text-foreground-700">
                      {p}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={600}>
              <p className="mt-9 inline-flex items-center gap-2 rounded-sm border border-dashed border-foreground-950/25 px-4 py-2 font-label text-[10px] uppercase tracking-label text-foreground-500">
                <i className="ri-information-line text-sm" />
                [ Add Locavore description here ]
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export function LocavorePeople() {
  return (
    <section className="relative overflow-hidden bg-foreground-950 text-background-50 grain">
      <div className="relative z-10 w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="eyebrow text-secondary-300">The People</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="mt-5 font-heading text-3xl leading-[1.05] sm:text-4xl md:text-5xl">
                Built by{" "}
                <span className="italic text-secondary-300">people</span>, not
                platforms.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-background-50/70">
                A locavore movement only works if it is full of real people —
                the crews, the families and the neighbours who cook, grow and
                trade close to home. Locavore PODS is where they meet.
              </p>
            </Reveal>
            <Reveal delay={280}>
              <a
                href={externalLinks.locavorePods}
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline mt-9 inline-flex w-fit items-center gap-2 font-label text-[11px] uppercase tracking-label text-secondary-300"
              >
                locavorepods.com
                <i className="ri-arrow-right-up-line text-base" />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal variant="mask">
              <div className="aspect-[4/3] w-full overflow-hidden bg-foreground-900 image-zoom">
                <img
                  src={images.kitchenCrew}
                  alt="A kitchen crew together"
                  title="The people of the Locavore movement"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

const chapters = [
  {
    no: "01",
    title: "The Places",
    note: "Markets, farms and the streets where a local food culture actually lives.",
    image: images.marketLane,
    alt: "An old-town market lane at dusk",
  },
  {
    no: "02",
    title: "The Food",
    note: "What grows here, cooked here — seasonal, honest and rooted in a region.",
    image: images.locavoreMarket,
    alt: "Fresh local produce on a market table",
  },
  {
    no: "03",
    title: "The Land",
    note: "The soil, the seasons and the growers who plan dinner a year in advance.",
    image: images.locavoreFarm,
    alt: "Rows of vegetables on a farm at dawn",
  },
];

export function LocavoreChapters() {
  return (
    <section className="bg-background-50">
      <div className="w-full px-5 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow text-secondary-700">The Ecosystem</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="mt-5 font-heading text-3xl leading-[1.05] text-foreground-950 sm:text-4xl md:text-5xl">
                Places. Food.{" "}
                <span className="italic text-secondary-700">Land.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:self-end">
            <Reveal delay={200}>
              <p className="font-body text-base leading-relaxed text-foreground-700">
                Three parts of one story — the geography of a locavore life.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-3">
          {chapters.map((c, i) => (
            <Reveal key={c.no} delay={i * 110}>
              <article className="group">
                <div className="aspect-[4/5] w-full overflow-hidden bg-foreground-900 image-zoom">
                  <img
                    src={c.image}
                    alt={c.alt}
                    title={c.title}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div className="mt-5 flex items-baseline gap-4">
                  <span className="font-heading text-lg italic text-secondary-600">
                    {c.no}
                  </span>
                  <h3 className="font-heading text-2xl text-foreground-950">
                    {c.title}
                  </h3>
                </div>
                <p className="mt-2 font-body text-sm leading-relaxed text-foreground-700">
                  {c.note}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}